import { Injectable } from '@angular/core'
import { environment } from '../../../environments/environment'

@Injectable({
  providedIn: 'root',
})
export class ObservabilityService {
  private datadogRumInitialized = false
  private datadogLogsInitialized = false
  private datadogLogs: { logger: { error: (message: string, messageContext?: object, error?: Error) => void } } | null = null

  /**
   * Lazy load and initialize Datadog RUM
   */
  async initializeRum(version: string): Promise<void> {
    if (this.datadogRumInitialized || !environment.ddApplicationId) {
      console.log('Datadog RUM initialization skipped:', {
        alreadyInitialized: this.datadogRumInitialized,
        hasApplicationId: !!environment.ddApplicationId,
        environment: environment.ddEnv,
      })
      return
    }

    console.log('Initializing Datadog RUM with config:', {
      applicationId: environment.ddApplicationId,
      service: environment.ddService,
      env: environment.ddEnv,
      version: version,
      sessionSampleRate: environment.ddRumSampleRate,
      environment: environment.ddEnv,
    })

    try {
      const { datadogRum } = await import('@datadog/browser-rum')

      datadogRum.init({
        applicationId: environment.ddApplicationId,
        clientToken: environment.ddClientToken,
        site: 'datadoghq.com',
        service: environment.ddService,
        env: environment.ddEnv,
        version: version,
        sessionSampleRate: environment.ddRumSampleRate,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
      })

      this.datadogRumInitialized = true
      console.log('Datadog RUM initialized successfully', {
        service: environment.ddService,
        env: environment.ddEnv,
        sampleRate: environment.ddRumSampleRate,
      })
    } catch (error) {
      console.warn('Failed to initialize Datadog RUM:', error)
    }
  }

  /**
   * Lazy load and initialize Datadog Logs
   */
  async initializeLogs(): Promise<void> {
    if (this.datadogLogsInitialized || !environment.ddClientToken) {
      return
    }

    try {
      const { datadogLogs } = await import('@datadog/browser-logs')

      datadogLogs.init({
        clientToken: environment.ddClientToken,
        site: 'datadoghq.com',
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ['error'],
        forwardReports: 'all',
        sessionSampleRate: 100,
        telemetrySampleRate: 0,
        sendLogsAfterSessionExpiration: false,
        useSecureSessionCookie: true,
        trackSessionAcrossSubdomains: true,
        service: environment.ddService,
      })

      datadogLogs.logger.setLevel('error')
      this.datadogLogs = datadogLogs
      this.datadogLogsInitialized = true
    } catch (error) {
      console.warn('Failed to initialize Datadog Logs:', error)
    }
  }

  /**
   * Log an error message (lazy loads Datadog if not already loaded)
   */
  async logError(message: string, context?: object): Promise<void> {
    if (!this.datadogLogsInitialized) {
      await this.initializeLogs()
    }

    if (this.datadogLogs?.logger) {
      this.datadogLogs.logger.error(message, context)
    } else {
      // Fallback to console if Datadog fails to load
      console.error(message, context)
    }
  }

  /**
   * Initialize both RUM and Logs (typically called from app component)
   */
  async initializeAll(version: string): Promise<void> {
    await Promise.all([this.initializeRum(version), this.initializeLogs()])
  }
}
